import {
	BUILDER_FEES_BPS,
	bytes32Zero,
	END_CURSOR,
	INITIAL_CURSOR,
	ORDER_VERSION_MISMATCH_ERROR,
} from "./constants.js";
import {
	ARE_ORDERS_SCORING,
	CANCEL_ALL,
	CANCEL_MARKET_ORDERS,
	CANCEL_ORDER,
	CANCEL_ORDERS,
	CLOSED_ONLY,
	CREATE_API_KEY,
	CREATE_BUILDER_API_KEY,
	CREATE_READONLY_API_KEY,
	DELETE_API_KEY,
	DELETE_READONLY_API_KEY,
	DERIVE_API_KEY,
	DROP_NOTIFICATIONS,
	GET_API_KEYS,
	GET_BALANCE_ALLOWANCE,
	GET_BUILDER_API_KEYS,
	GET_BUILDER_FEES,
	GET_BUILDER_TRADES,
	GET_CLOB_MARKET,
	GET_EARNINGS_FOR_USER_FOR_DAY,
	GET_FEE_RATE,
	GET_LAST_TRADE_PRICE,
	GET_LAST_TRADES_PRICES,
	GET_LIQUIDITY_REWARD_PERCENTAGES,
	GET_MARKET,
	GET_MARKET_BY_TOKEN,
	GET_MARKET_TRADES_EVENTS,
	GET_MARKETS,
	GET_MIDPOINT,
	GET_MIDPOINTS,
	GET_NEG_RISK,
	GET_NOTIFICATIONS,
	GET_OPEN_ORDERS,
	GET_ORDER,
	GET_ORDER_BOOK,
	GET_ORDER_BOOKS,
	GET_PRE_MIGRATION_ORDERS,
	GET_PRICE,
	GET_PRICES,
	GET_PRICES_HISTORY,
	GET_READONLY_API_KEYS,
	GET_REWARDS_EARNINGS_PERCENTAGES,
	GET_REWARDS_MARKETS,
	GET_REWARDS_MARKETS_CURRENT,
	GET_SAMPLING_MARKETS,
	GET_SAMPLING_SIMPLIFIED_MARKETS,
	GET_SIMPLIFIED_MARKETS,
	GET_SPREAD,
	GET_SPREADS,
	GET_TICK_SIZE,
	GET_TOTAL_EARNINGS_FOR_USER_FOR_DAY,
	GET_TRADES,
	HEARTBEAT,
	IS_ORDER_SCORING,
	OK,
	POST_ORDER,
	POST_ORDERS,
	REVOKE_BUILDER_API_KEY,
	TIME,
	UPDATE_BALANCE_ALLOWANCE,
} from "./endpoints.js";
import { ApiError, L1_AUTH_UNAVAILABLE_ERROR, L2_AUTH_NOT_AVAILABLE } from "./errors.js";
import { adjustBuyAmountForFees, validateFeeSlippage } from "./fees/index.js";
import { createL1Headers, createL2Headers } from "./headers/index.js";
import {
	DELETE,
	del,
	GET,
	get,
	POST,
	parseDropNotificationParams,
	post,
	type RequestOptions,
} from "./http-helpers/index.js";
import {
	calculateBuyMarketPrice,
	calculateSellMarketPrice,
	ROUNDING_CONFIG,
} from "./order-builder/helpers/index.js";
import { OrderBuilder } from "./order-builder/index.js";
import { SignatureTypeV2 } from "./order-utils/model/signatureTypeV2.js";
import type { ClobSigner } from "./signing/signer.js";
import type {
	ApiKeyCreds,
	ApiKeyRaw,
	ApiKeysResponse,
	BalanceAllowanceParams,
	BalanceAllowanceResponse,
	BanStatus,
	BookParams,
	BuilderApiKey,
	BuilderApiKeyResponse,
	BuilderConfig,
	BuilderFeeRates,
	BuilderTrade,
	BuilderTradeParams,
	BuilderTradesResponse,
	Chain,
	ClobErrorResponseBody,
	CreateOrderOptions,
	DropNotificationParams,
	FeeInfos,
	FeeRates,
	MarketDetails,
	MarketPrice,
	MarketReward,
	MarketTradeEvent,
	NegRisk,
	NewOrderV1,
	NewOrderV2,
	Notification,
	OpenOrder,
	OpenOrderParams,
	OpenOrdersResponse,
	OrderBookSummary,
	OrderMarketCancelParams,
	OrderPayload,
	OrderScoring,
	OrderScoringParams,
	OrdersScoring,
	OrdersScoringParams,
	PaginationPayload,
	PostOrdersArgs,
	PreMigrationOrder,
	PreMigrationOrdersResponse,
	PriceHistoryFilterParams,
	ReadonlyApiKeyResponse,
	RewardsPercentages,
	TickSize,
	TickSizes,
	TokenConditionMap,
	TotalUserEarning,
	Trade,
	TradeParams,
	TradesPaginatedResponse,
	UserEarning,
	UserMarketOrderV1,
	UserMarketOrderV2,
	UserOrderV1,
	UserOrderV2,
	UserRewardsEarning,
} from "./types/index.js";
import { OrderType, orderToJsonV1, orderToJsonV2, Side } from "./types/index.js";
import { isV2Order, type SignedOrder } from "./types/unifiedOrder.js";
import {
	generateOrderBookSummaryHash,
	isTickSizeSmaller,
	priceValid,
	roundNormal,
} from "./utilities.js";

export { adjustBuyAmountForFees } from "./fees/index.js";

export interface ClobClientOptions {
	host: string;
	chain: Chain;
	signer?: ClobSigner;
	creds?: ApiKeyCreds;
	signatureType?: SignatureTypeV2;
	funderAddress?: string;
	useServerTime?: boolean;
	builderConfig?: BuilderConfig;
	getSigner?: () => Promise<ClobSigner> | ClobSigner;
	retryOnError?: boolean;
	throwOnError?: boolean;
	feeSlippage?: number;
}

export class ClobClient {
	readonly host: string;

	readonly chainId: Chain;

	// Used to perform Level 1 authentication and sign orders
	readonly signer?: ClobSigner;

	// Used to perform Level 2 authentication
	readonly creds?: ApiKeyCreds;

	readonly orderBuilder: OrderBuilder;

	readonly tickSizes: TickSizes;

	readonly negRisk: NegRisk;

	readonly feeInfos: FeeInfos;

	// Fee rate bps data for CLOB V1
	readonly feeRates: FeeRates;

	readonly builderFeeRates: BuilderFeeRates;

	private readonly tokenConditionMap: TokenConditionMap;

	readonly useServerTime?: boolean;

	readonly builderConfig?: BuilderConfig;

	readonly signatureType: SignatureTypeV2;

	readonly funderAddress?: string;

	private cachedVersion?: number;

	readonly retryOnError?: boolean;

	readonly throwOnError?: boolean;

	readonly feeSlippage: number;

	constructor({
		host,
		chain,
		signer,
		creds,
		signatureType,
		funderAddress,
		useServerTime,
		builderConfig,
		getSigner,
		retryOnError,
		throwOnError,
		feeSlippage,
	}: ClobClientOptions) {
		this.host = host.endsWith("/") ? host.slice(0, -1) : host;
		this.chainId = chain;

		if (signer !== undefined) {
			this.signer = signer;
		}
		if (creds !== undefined) {
			this.creds = creds;
		}
		this.orderBuilder = new OrderBuilder(
			signer as ClobSigner,
			chain,
			signatureType,
			funderAddress,
			getSigner,
		);
		this.signatureType = signatureType ?? SignatureTypeV2.EOA;
		this.funderAddress = funderAddress;
		this.tickSizes = {};
		this.negRisk = {};
		this.feeRates = {};
		this.feeInfos = {};
		this.builderFeeRates = {};
		this.tokenConditionMap = {};
		this.retryOnError = retryOnError;
		this.throwOnError = throwOnError;
		this.feeSlippage = feeSlippage ?? 0;
		validateFeeSlippage(this.feeSlippage);
		this.useServerTime = useServerTime;
		if (builderConfig !== undefined) {
			this.builderConfig = builderConfig;
		}
	}

	// Public endpoints
	public async getOk(): Promise<any> {
		return this.get(`${this.host}${OK}`);
	}

	public async postHeartbeat(
		heartbeatId = "",
	): Promise<{ heartbeat_id: string; error_msg?: string }> {
		this.canL2Auth();
		const body = JSON.stringify({ heartbeat_id: heartbeatId });
		const headers = await createL2Headers(
			this.signer as ClobSigner,
			this.creds as ApiKeyCreds,
			{ method: POST, requestPath: HEARTBEAT, body },
			this.useServerTime ? await this.getServerTime() : undefined,
		);
		return this.post(`${this.host}${HEARTBEAT}`, {
			headers,
			data: { heartbeat_id: heartbeatId },
		});
	}

	public async getVersion(): Promise<number> {
		const response = await this.get(`${this.host}/version`);
		// default to v2
		return response?.version ?? 2;
	}

	public async getServerTime(): Promise<number> {
		return this.get(`${this.host}${TIME}`);
	}

	public async getSamplingSimplifiedMarkets(
		next_cursor = INITIAL_CURSOR,
	): Promise<PaginationPayload> {
		return this.get(`${this.host}${GET_SAMPLING_SIMPLIFIED_MARKETS}`, {
			params: { next_cursor },
		});
	}

	public async getSamplingMarkets(next_cursor = INITIAL_CURSOR): Promise<PaginationPayload> {
		return this.get(`${this.host}${GET_SAMPLING_MARKETS}`, {
			params: { next_cursor },
		});
	}

	public async getSimplifiedMarkets(next_cursor = INITIAL_CURSOR): Promise<PaginationPayload> {
		return this.get(`${this.host}${GET_SIMPLIFIED_MARKETS}`, {
			params: { next_cursor },
		});
	}

	public async getMarkets(next_cursor = INITIAL_CURSOR): Promise<PaginationPayload> {
		return this.get(`${this.host}${GET_MARKETS}`, {
			params: { next_cursor },
		});
	}

	public async getMarket(conditionID: string): Promise<any> {
		return this.get(`${this.host}${GET_MARKET}${conditionID}`);
	}

	public async getClobMarketInfo(conditionID: string): Promise<MarketDetails> {
		const result: MarketDetails = await this.get(
			`${this.host}${GET_CLOB_MARKET}${conditionID}`,
		);

		if (!result?.t) {
			throw new Error(`failed to fetch market info for condition id ${conditionID}`);
		}

		for (const token of result.t) {
			if (!token) continue;
			const tokenId = token.t;

			this.tokenConditionMap[tokenId] = conditionID;
			this.tickSizes[tokenId] = result.mts.toString() as TickSize;
			this.negRisk[tokenId] = result.nr ?? false;

			this.feeInfos[tokenId] = {
				rate: result.fd?.r ?? 0,
				exponent: result.fd?.e ?? 0,
			};
		}

		return result;
	}

	public async getOrderBook(tokenID: string): Promise<OrderBookSummary> {
		return this.get(`${this.host}${GET_ORDER_BOOK}`, {
			params: { token_id: tokenID },
		});
	}

	public async getOrderBooks(params: BookParams[]): Promise<OrderBookSummary[]> {
		return this.post(`${this.host}${GET_ORDER_BOOKS}`, {
			data: params,
		});
	}

	public async getTickSize(tokenID: string): Promise<TickSize> {
		if (tokenID in this.tickSizes) {
			return this.tickSizes[tokenID];
		}

		if (tokenID in this.tokenConditionMap) {
			await this.getClobMarketInfo(this.tokenConditionMap[tokenID]);
			return this.tickSizes[tokenID];
		}

		const result = await this.get(`${this.host}${GET_TICK_SIZE}`, {
			params: { token_id: tokenID },
		});
		this.tickSizes[tokenID] = result.minimum_tick_size.toString() as TickSize;

		return this.tickSizes[tokenID];
	}

	public async getNegRisk(tokenID: string): Promise<boolean> {
		if (tokenID in this.negRisk) {
			return this.negRisk[tokenID];
		}

		if (tokenID in this.tokenConditionMap) {
			await this.getClobMarketInfo(this.tokenConditionMap[tokenID]);
			return this.negRisk[tokenID];
		}

		const result = await this.get(`${this.host}${GET_NEG_RISK}`, {
			params: { token_id: tokenID },
		});
		this.negRisk[tokenID] = result.neg_risk as boolean;

		return this.negRisk[tokenID];
	}

	public async getFeeRateBps(tokenID: string): Promise<number> {
		if (tokenID in this.feeRates) {
			return this.feeRates[tokenID];
		}

		const result = await this.get(`${this.host}${GET_FEE_RATE}`, {
			params: { token_id: tokenID },
		});
		this.feeRates[tokenID] = result.base_fee as number;

		return this.feeRates[tokenID];
	}

	public async getFeeExponent(tokenID: string): Promise<number> {
		if (tokenID in this.feeInfos) {
			return this.feeInfos[tokenID].exponent;
		}

		await this._ensureMarketInfoCached(tokenID);
		return this.feeInfos[tokenID].exponent;
	}

	/**
	 * Calculates the hash for the given orderbook
	 * @param orderbook
	 * @returns
	 */
	public async getOrderBookHash(orderbook: OrderBookSummary): Promise<string> {
		return generateOrderBookSummaryHash(orderbook);
	}

	public async getMidpoint(tokenID: string): Promise<any> {
		return this.get(`${this.host}${GET_MIDPOINT}`, {
			params: { token_id: tokenID },
		});
	}

	public async getMidpoints(params: BookParams[]): Promise<any> {
		return this.post(`${this.host}${GET_MIDPOINTS}`, {
			data: params,
		});
	}

	public async getPrice(tokenID: string, side: string): Promise<any> {
		return this.get(`${this.host}${GET_PRICE}`, {
			params: { token_id: tokenID, side: side },
		});
	}

	public async getPrices(params: BookParams[]): Promise<any> {
		return this.post(`${this.host}${GET_PRICES}`, {
			data: params,
		});
	}

	public async getSpread(tokenID: string): Promise<any> {
		return this.get(`${this.host}${GET_SPREAD}`, {
			params: { token_id: tokenID },
		});
	}

	public async getSpreads(params: BookParams[]): Promise<any> {
		return this.post(`${this.host}${GET_SPREADS}`, {
			data: params,
		});
	}

	public async getLastTradePrice(tokenID: string): Promise<any> {
		return this.get(`${this.host}${GET_LAST_TRADE_PRICE}`, {
			params: { token_id: tokenID },
		});
	}

	public async getLastTradesPrices(params: BookParams[]): Promise<any> {
		return this.post(`${this.host}${GET_LAST_TRADES_PRICES}`, {
			data: params,
		});
	}

	public async getPricesHistory(params: PriceHistoryFilterParams): Promise<MarketPrice[]> {
		if (!params.interval && (params.startTs === undefined || params.endTs === undefined)) {
			throw new Error("getPricesHistory requires either interval or both startTs and endTs");
		}
		return this.get(`${this.host}${GET_PRICES_HISTORY}`, {
			params,
		});
	}

	// L1 Authed

	/**
	 * Creates a new API key for a user
	 * @param nonce
	 * @returns ApiKeyCreds
	 */
	public async createApiKey(nonce?: number): Promise<ApiKeyCreds> {
		this.canL1Auth();

		const endpoint = `${this.host}${CREATE_API_KEY}`;
		const headers = await createL1Headers(
			this.signer as ClobSigner,
			this.chainId,
			nonce,
			this.useServerTime ? await this.getServerTime() : undefined,
		);

		return await this.post(endpoint, { headers }).then((apiKeyRaw: ApiKeyRaw) => {
			const apiKey: ApiKeyCreds = {
				key: apiKeyRaw.apiKey,
				secret: apiKeyRaw.secret,
				passphrase: apiKeyRaw.passphrase,
			};
			return apiKey;
		});
	}

	/**
	 * Derives an existing API key for a user
	 * @param nonce
	 * @returns ApiKeyCreds
	 */
	public async deriveApiKey(nonce?: number): Promise<ApiKeyCreds> {
		this.canL1Auth();

		const endpoint = `${this.host}${DERIVE_API_KEY}`;
		const headers = await createL1Headers(
			this.signer as ClobSigner,
			this.chainId,
			nonce,
			this.useServerTime ? await this.getServerTime() : undefined,
		);

		return await this.get(endpoint, { headers }).then((apiKeyRaw: ApiKeyRaw) => {
			const apiKey: ApiKeyCreds = {
				key: apiKeyRaw.apiKey,
				secret: apiKeyRaw.secret,
				passphrase: apiKeyRaw.passphrase,
			};
			return apiKey;
		});
	}

	public async createOrDeriveApiKey(nonce?: number): Promise<ApiKeyCreds> {
		return this.createApiKey(nonce).then(response => {
			if (!response.key) {
				return this.deriveApiKey(nonce);
			}
			return response;
		});
	}

	public async getApiKeys(): Promise<ApiKeysResponse> {
		this.canL2Auth();

		const endpoint = GET_API_KEYS;
		const headerArgs = {
			method: GET,
			requestPath: endpoint,
		};

		const headers = await createL2Headers(
			this.signer as ClobSigner,
			this.creds as ApiKeyCreds,
			headerArgs,
			this.useServerTime ? await this.getServerTime() : undefined,
		);

		return this.get(`${this.host}${endpoint}`, { headers });
	}

	public async getClosedOnlyMode(): Promise<BanStatus> {
		this.canL2Auth();

		const endpoint = CLOSED_ONLY;
		const headerArgs = {
			method: GET,
			requestPath: endpoint,
		};

		const headers = await createL2Headers(
			this.signer as ClobSigner,
			this.creds as ApiKeyCreds,
			headerArgs,
			this.useServerTime ? await this.getServerTime() : undefined,
		);

		return this.get(`${this.host}${endpoint}`, { headers });
	}

	public async deleteApiKey(): Promise<any> {
		this.canL2Auth();

		const endpoint = DELETE_API_KEY;
		const headerArgs = {
			method: DELETE,
			requestPath: endpoint,
		};

		const headers = await createL2Headers(
			this.signer as ClobSigner,
			this.creds as ApiKeyCreds,
			headerArgs,
			this.useServerTime ? await this.getServerTime() : undefined,
		);

		return this.del(`${this.host}${endpoint}`, { headers });
	}

	public async createReadonlyApiKey(): Promise<ReadonlyApiKeyResponse> {
		this.canL2Auth();

		const endpoint = CREATE_READONLY_API_KEY;
		const headerArgs = {
			method: POST,
			requestPath: endpoint,
		};

		const headers = await createL2Headers(
			this.signer as ClobSigner,
			this.creds as ApiKeyCreds,
			headerArgs,
			this.useServerTime ? await this.getServerTime() : undefined,
		);

		return this.post(`${this.host}${endpoint}`, { headers });
	}

	public async getReadonlyApiKeys(): Promise<string[]> {
		this.canL2Auth();

		const endpoint = GET_READONLY_API_KEYS;
		const headerArgs = {
			method: GET,
			requestPath: endpoint,
		};

		const headers = await createL2Headers(
			this.signer as ClobSigner,
			this.creds as ApiKeyCreds,
			headerArgs,
			this.useServerTime ? await this.getServerTime() : undefined,
		);

		return this.get(`${this.host}${endpoint}`, { headers });
	}

	public async deleteReadonlyApiKey(key: string): Promise<boolean> {
		this.canL2Auth();

		const endpoint = DELETE_READONLY_API_KEY;
		const payload = { key };
		const headerArgs = {
			method: DELETE,
			requestPath: endpoint,
			body: JSON.stringify(payload),
		};

		const headers = await createL2Headers(
			this.signer as ClobSigner,
			this.creds as ApiKeyCreds,
			headerArgs,
			this.useServerTime ? await this.getServerTime() : undefined,
		);

		return this.del(`${this.host}${endpoint}`, { headers, data: payload });
	}

	public async getOrder(orderID: string): Promise<OpenOrder> {
		this.canL2Auth();

		const endpoint = `${GET_ORDER}${orderID}`;
		const headerArgs = {
			method: GET,
			requestPath: endpoint,
		};

		const headers = await createL2Headers(
			this.signer as ClobSigner,
			this.creds as ApiKeyCreds,
			headerArgs,
			this.useServerTime ? await this.getServerTime() : undefined,
		);

		return this.get(`${this.host}${endpoint}`, { headers });
	}

	public async getTrades(
		params?: TradeParams,
		only_first_page = false,
		next_cursor?: string,
	): Promise<Trade[]> {
		this.canL2Auth();

		const endpoint = GET_TRADES;
		const headerArgs = {
			method: GET,
			requestPath: endpoint,
		};

		const headers = await createL2Headers(
			this.signer as ClobSigner,
			this.creds as ApiKeyCreds,
			headerArgs,
			this.useServerTime ? await this.getServerTime() : undefined,
		);

		let results: Trade[] = [];
		next_cursor = next_cursor || INITIAL_CURSOR;
		while (next_cursor !== END_CURSOR && (next_cursor === INITIAL_CURSOR || !only_first_page)) {
			const _params: any = {
				...params,
				next_cursor,
			};
			const response = await this.get(`${this.host}${endpoint}`, {
				headers,
				params: _params,
			});
			next_cursor = response.next_cursor;
			results = [...results, ...response.data];
		}
		return results;
	}

	public async getTradesPaginated(
		params?: TradeParams,
		next_cursor?: string,
	): Promise<TradesPaginatedResponse> {
		this.canL2Auth();

		const endpoint = GET_TRADES;
		const headerArgs = {
			method: GET,
			requestPath: endpoint,
		};

		const headers = await createL2Headers(
			this.signer as ClobSigner,
			this.creds as ApiKeyCreds,
			headerArgs,
			this.useServerTime ? await this.getServerTime() : undefined,
		);

		next_cursor = next_cursor || INITIAL_CURSOR;

		const _params: any = { ...params, next_cursor };

		const {
			data,
			...rest
		}: { data: Trade[]; next_cursor: string; limit: number; count: number } = await this.get(
			`${this.host}${endpoint}`,
			{ headers, params: _params },
		);

		return { trades: Array.isArray(data) ? [...data] : [], ...rest };
	}

	public async getBuilderTrades(
		params: BuilderTradeParams,
		next_cursor?: string,
	): Promise<BuilderTradesResponse> {
		if (!params.builder_code || params.builder_code === bytes32Zero) {
			throw new Error("builderCode is required and cannot be zero");
		}

		const _params: any = { ...params, next_cursor: next_cursor || INITIAL_CURSOR };

		const {
			data,
			...rest
		}: { data: BuilderTrade[]; next_cursor: string; limit: number; count: number } =
			await this.get(`${this.host}${GET_BUILDER_TRADES}`, { params: _params });

		return { trades: Array.isArray(data) ? [...data] : [], ...rest };
	}

	public async getNotifications(): Promise<Notification[]> {
		this.canL2Auth();

		const endpoint = GET_NOTIFICATIONS;
		const headerArgs = {
			method: GET,
			requestPath: endpoint,
		};

		const headers = await createL2Headers(
			this.signer as ClobSigner,
			this.creds as ApiKeyCreds,
			headerArgs,
			this.useServerTime ? await this.getServerTime() : undefined,
		);

		return this.get(`${this.host}${endpoint}`, {
			headers,
			params: { signature_type: this.orderBuilder.signatureType },
		});
	}

	public async dropNotifications(params?: DropNotificationParams): Promise<void> {
		this.canL2Auth();

		const endpoint = DROP_NOTIFICATIONS;
		const l2HeaderArgs = {
			method: DELETE,
			requestPath: endpoint,
		};

		const headers = await createL2Headers(
			this.signer as ClobSigner,
			this.creds as ApiKeyCreds,
			l2HeaderArgs,
			this.useServerTime ? await this.getServerTime() : undefined,
		);

		return this.del(`${this.host}${endpoint}`, {
			headers,
			params: parseDropNotificationParams(params),
		});
	}

	public async getBalanceAllowance(
		params?: BalanceAllowanceParams,
	): Promise<BalanceAllowanceResponse> {
		this.canL2Auth();

		const endpoint = GET_BALANCE_ALLOWANCE;
		const headerArgs = {
			method: GET,
			requestPath: endpoint,
		};

		const headers = await createL2Headers(
			this.signer as ClobSigner,
			this.creds as ApiKeyCreds,
			headerArgs,
			this.useServerTime ? await this.getServerTime() : undefined,
		);

		const _params = {
			...params,
			signature_type: this.orderBuilder.signatureType,
		};

		return this.get(`${this.host}${endpoint}`, { headers, params: _params });
	}

	public async updateBalanceAllowance(params?: BalanceAllowanceParams): Promise<void> {
		this.canL2Auth();

		const endpoint = UPDATE_BALANCE_ALLOWANCE;
		const headerArgs = {
			method: GET,
			requestPath: endpoint,
		};

		const headers = await createL2Headers(
			this.signer as ClobSigner,
			this.creds as ApiKeyCreds,
			headerArgs,
			this.useServerTime ? await this.getServerTime() : undefined,
		);

		const _params = {
			...params,
			signature_type: this.orderBuilder.signatureType,
		};

		return this.get(`${this.host}${endpoint}`, { headers, params: _params });
	}

	public async createOrder(
		userOrder: UserOrderV1 | UserOrderV2,
		options?: Partial<CreateOrderOptions>,
	): Promise<SignedOrder> {
		this.canL1Auth();

		const orderToSign = { ...userOrder };

		if (this.builderConfig?.builderCode && !orderToSign.builderCode) {
			orderToSign.builderCode = this.builderConfig.builderCode;
		}

		const { tokenID } = orderToSign;

		const tickSize = await this._resolveTickSize(tokenID, options?.tickSize);

		if (!priceValid(orderToSign.price, tickSize)) {
			throw new Error(
				`invalid price (${orderToSign.price}), min: ${parseFloat(tickSize)} - max: ${
					1 - parseFloat(tickSize)
				}`,
			);
		}
		orderToSign.price = roundNormal(orderToSign.price, ROUNDING_CONFIG[tickSize].price);

		const version = await this.resolveVersion();

		if (
			version === 2 &&
			orderToSign.side === Side.BUY &&
			"userUSDCBalance" in orderToSign &&
			orderToSign.userUSDCBalance !== undefined
		) {
			const adjustedAmount = await this.adjustBuyAmountForBalance(
				tokenID,
				orderToSign.size * orderToSign.price,
				orderToSign.price,
				orderToSign.userUSDCBalance,
				orderToSign.builderCode,
			);
			orderToSign.size = adjustedAmount / orderToSign.price;
		}

		const negRisk = options?.negRisk ?? (await this.getNegRisk(tokenID));

		if (version === 1) {
			const userFeeRateBps =
				"feeRateBps" in orderToSign ? (orderToSign as UserOrderV1).feeRateBps : undefined;
			const feeRateBps = await this._resolveFeeRateBps(tokenID, userFeeRateBps);
			(orderToSign as UserOrderV1).feeRateBps = feeRateBps;
		}

		return this.orderBuilder.buildOrder(
			orderToSign,
			{
				tickSize,
				negRisk,
			},
			version,
		);
	}

	public async createMarketOrder(
		userMarketOrder: UserMarketOrderV1 | UserMarketOrderV2,
		options?: Partial<CreateOrderOptions>,
	): Promise<SignedOrder> {
		this.canL1Auth();

		const { tokenID } = userMarketOrder;

		await this._ensureMarketInfoCached(tokenID);

		const tickSize = await this._resolveTickSize(tokenID, options?.tickSize);

		if (!userMarketOrder.price) {
			userMarketOrder.price = await this.calculateMarketPrice(
				tokenID,
				userMarketOrder.side,
				userMarketOrder.amount,
				userMarketOrder.orderType,
			);
		}

		if (!priceValid(userMarketOrder.price, tickSize)) {
			throw new Error(
				`invalid price (${userMarketOrder.price}), min: ${parseFloat(tickSize)} - max: ${
					1 - parseFloat(tickSize)
				}`,
			);
		}

		const orderToSign = { ...userMarketOrder };

		if (this.builderConfig?.builderCode && !orderToSign.builderCode) {
			orderToSign.builderCode = this.builderConfig.builderCode;
		}

		await this.ensureBuilderFeeRateCached(orderToSign.builderCode);

		if (
			orderToSign.side === Side.BUY &&
			"userUSDCBalance" in orderToSign &&
			orderToSign.userUSDCBalance !== undefined
		) {
			// biome-ignore lint/style/noNonNullAssertion: price is validated above
			const price = orderToSign.price!;
			orderToSign.amount = await this.adjustBuyAmountForBalance(
				tokenID,
				orderToSign.amount,
				price,
				orderToSign.userUSDCBalance,
				orderToSign.builderCode,
			);
		}

		const negRisk = options?.negRisk ?? (await this.getNegRisk(tokenID));
		const version = await this.resolveVersion();

		if (version === 1) {
			const userFeeRateBps =
				"feeRateBps" in orderToSign
					? (orderToSign as UserMarketOrderV1).feeRateBps
					: undefined;
			const feeRateBps = await this._resolveFeeRateBps(tokenID, userFeeRateBps);
			(orderToSign as UserMarketOrderV1).feeRateBps = feeRateBps;
		}

		return this.orderBuilder.buildMarketOrder(
			orderToSign,
			{
				tickSize,
				negRisk,
			},
			version,
		);
	}

	public async createAndPostOrder<T extends OrderType.GTC | OrderType.GTD = OrderType.GTC>(
		userOrder: UserOrderV1 | UserOrderV2,
		options?: Partial<CreateOrderOptions>,
		orderType: T = OrderType.GTC as T,
		postOnly = false,
		deferExec = false,
	): Promise<any> {
		let postOrderResponse: any;

		await this._retryOnVersionUpdate(async () => {
			const order = await this.createOrder(userOrder, options);
			postOrderResponse = await this.postOrder(order, orderType, postOnly, deferExec);
		});

		return postOrderResponse;
	}

	public async createAndPostMarketOrder<T extends OrderType.FOK | OrderType.FAK = OrderType.FOK>(
		userMarketOrder: UserMarketOrderV1 | UserMarketOrderV2,
		options?: Partial<CreateOrderOptions>,
		orderType: T = OrderType.FOK as T,
		deferExec = false,
	): Promise<any> {
		let postOrderMarketResponse: any;

		await this._retryOnVersionUpdate(async () => {
			const order = await this.createMarketOrder(userMarketOrder, options);
			postOrderMarketResponse = await this.postOrder(order, orderType, false, deferExec);
		});

		return postOrderMarketResponse;
	}

	public async getOpenOrders(
		params?: OpenOrderParams,
		only_first_page = false,
		next_cursor?: string,
	): Promise<OpenOrdersResponse> {
		this.canL2Auth();
		const endpoint = GET_OPEN_ORDERS;
		const l2HeaderArgs = {
			method: GET,
			requestPath: endpoint,
		};

		const headers = await createL2Headers(
			this.signer as ClobSigner,
			this.creds as ApiKeyCreds,
			l2HeaderArgs,
			this.useServerTime ? await this.getServerTime() : undefined,
		);

		let results: OpenOrder[] = [];
		next_cursor = next_cursor || INITIAL_CURSOR;
		while (next_cursor !== END_CURSOR && (next_cursor === INITIAL_CURSOR || !only_first_page)) {
			const _params: any = {
				...params,
				next_cursor,
			};
			const response = await this.get(`${this.host}${endpoint}`, {
				headers,
				params: _params,
			});
			next_cursor = response.next_cursor;
			results = [...results, ...response.data];
		}
		return results;
	}

	public async getPreMigrationOrders(
		only_first_page = false,
		next_cursor?: string,
	): Promise<PreMigrationOrdersResponse> {
		this.canL2Auth();
		const endpoint = GET_PRE_MIGRATION_ORDERS;
		const l2HeaderArgs = {
			method: GET,
			requestPath: endpoint,
		};

		const headers = await createL2Headers(
			this.signer as ClobSigner,
			this.creds as ApiKeyCreds,
			l2HeaderArgs,
			this.useServerTime ? await this.getServerTime() : undefined,
		);

		let results: PreMigrationOrder[] = [];
		next_cursor = next_cursor || INITIAL_CURSOR;
		while (next_cursor !== END_CURSOR && (next_cursor === INITIAL_CURSOR || !only_first_page)) {
			const _params: any = { next_cursor };
			const response = await this.get(`${this.host}${endpoint}`, {
				headers,
				params: _params,
			});
			next_cursor = response.next_cursor;
			results = [...results, ...response.data];
		}
		return results;
	}

	public async postOrder<T extends OrderType = OrderType.GTC>(
		order: SignedOrder,
		orderType: T = OrderType.GTC as T,
		postOnly = false,
		deferExec = false,
	): Promise<any> {
		this.canL2Auth();
		if (postOnly && (orderType === OrderType.FOK || orderType === OrderType.FAK)) {
			throw new Error("postOnly is not supported for FOK/FAK orders");
		}
		const endpoint = POST_ORDER;

		const orderPayload = isV2Order(order)
			? orderToJsonV2(order, this.creds?.key || "", orderType, postOnly, deferExec)
			: orderToJsonV1(order, this.creds?.key || "", orderType, postOnly, deferExec);

		const l2HeaderArgs = {
			method: POST,
			requestPath: endpoint,
			body: JSON.stringify(orderPayload),
		};

		const headers = await createL2Headers(
			this.signer as ClobSigner,
			this.creds as ApiKeyCreds,
			l2HeaderArgs,
			this.useServerTime ? await this.getServerTime() : undefined,
		);

		const res = await this.post(
			`${this.host}${endpoint}`,
			{
				headers,
				data: orderPayload,
			},
			true,
		);

		if (this._isOrderVersionMismatch(res)) await this.resolveVersion(true);

		return this.throwIfError(res);
	}

	public async postOrders(
		args: PostOrdersArgs[],
		postOnly = false,
		deferExec = false,
	): Promise<any> {
		this.canL2Auth();
		if (
			postOnly &&
			args.some(({ orderType }) => orderType === OrderType.FOK || orderType === OrderType.FAK)
		) {
			throw new Error("postOnly is not supported for FOK/FAK orders");
		}
		const endpoint = POST_ORDERS;
		const ordersPayload: (NewOrderV2<any> | NewOrderV1<any>)[] = [];
		for (const arg of args) {
			const { order, orderType } = arg;
			// Version-aware dispatch
			const orderPayload = isV2Order(order)
				? orderToJsonV2(order, this.creds?.key || "", orderType, postOnly, deferExec)
				: orderToJsonV1(order, this.creds?.key || "", orderType, postOnly, deferExec);
			ordersPayload.push(orderPayload);
		}

		const l2HeaderArgs = {
			method: POST,
			requestPath: endpoint,
			body: JSON.stringify(ordersPayload),
		};

		const headers = await createL2Headers(
			this.signer as ClobSigner,
			this.creds as ApiKeyCreds,
			l2HeaderArgs,
			this.useServerTime ? await this.getServerTime() : undefined,
		);

		const res = await this.post(
			`${this.host}${endpoint}`,
			{
				headers,
				data: ordersPayload,
			},
			true,
		);

		if (this._isOrderVersionMismatch(res)) await this.resolveVersion(true);

		return this.throwIfError(res);
	}

	public async cancelOrder(payload: OrderPayload): Promise<any> {
		this.canL2Auth();
		const endpoint = CANCEL_ORDER;
		const l2HeaderArgs = {
			method: DELETE,
			requestPath: endpoint,
			body: JSON.stringify(payload),
		};

		const headers = await createL2Headers(
			this.signer as ClobSigner,
			this.creds as ApiKeyCreds,
			l2HeaderArgs,
			this.useServerTime ? await this.getServerTime() : undefined,
		);
		return this.del(`${this.host}${endpoint}`, { headers, data: payload });
	}

	public async cancelOrders(ordersHashes: string[]): Promise<any> {
		this.canL2Auth();
		const endpoint = CANCEL_ORDERS;
		const l2HeaderArgs = {
			method: DELETE,
			requestPath: endpoint,
			body: JSON.stringify(ordersHashes),
		};

		const headers = await createL2Headers(
			this.signer as ClobSigner,
			this.creds as ApiKeyCreds,
			l2HeaderArgs,
			this.useServerTime ? await this.getServerTime() : undefined,
		);
		return this.del(`${this.host}${endpoint}`, { headers, data: ordersHashes });
	}

	public async cancelAll(): Promise<any> {
		this.canL2Auth();
		const endpoint = CANCEL_ALL;
		const l2HeaderArgs = {
			method: DELETE,
			requestPath: endpoint,
		};

		const headers = await createL2Headers(
			this.signer as ClobSigner,
			this.creds as ApiKeyCreds,
			l2HeaderArgs,
			this.useServerTime ? await this.getServerTime() : undefined,
		);
		return this.del(`${this.host}${endpoint}`, { headers });
	}

	public async cancelMarketOrders(payload: OrderMarketCancelParams): Promise<any> {
		this.canL2Auth();
		const endpoint = CANCEL_MARKET_ORDERS;
		const l2HeaderArgs = {
			method: DELETE,
			requestPath: endpoint,
			body: JSON.stringify(payload),
		};

		const headers = await createL2Headers(
			this.signer as ClobSigner,
			this.creds as ApiKeyCreds,
			l2HeaderArgs,
			this.useServerTime ? await this.getServerTime() : undefined,
		);
		return this.del(`${this.host}${endpoint}`, { headers, data: payload });
	}

	public async isOrderScoring(params?: OrderScoringParams): Promise<OrderScoring> {
		this.canL2Auth();

		const endpoint = IS_ORDER_SCORING;
		const headerArgs = {
			method: GET,
			requestPath: endpoint,
		};

		const headers = await createL2Headers(
			this.signer as ClobSigner,
			this.creds as ApiKeyCreds,
			headerArgs,
			this.useServerTime ? await this.getServerTime() : undefined,
		);

		return this.get(`${this.host}${endpoint}`, { headers, params });
	}

	public async areOrdersScoring(params?: OrdersScoringParams): Promise<OrdersScoring> {
		this.canL2Auth();

		const endpoint = ARE_ORDERS_SCORING;
		const payload = JSON.stringify(params?.orderIds);
		const headerArgs = {
			method: POST,
			requestPath: endpoint,
			body: payload,
		};

		const headers = await createL2Headers(
			this.signer as ClobSigner,
			this.creds as ApiKeyCreds,
			headerArgs,
			this.useServerTime ? await this.getServerTime() : undefined,
		);

		return this.post(`${this.host}${endpoint}`, {
			headers,
			data: payload,
		});
	}

	// Rewards
	public async getEarningsForUserForDay(date: string): Promise<UserEarning[]> {
		this.canL2Auth();

		const endpoint = GET_EARNINGS_FOR_USER_FOR_DAY;
		const headerArgs = {
			method: GET,
			requestPath: endpoint,
		};

		const headers = await createL2Headers(
			this.signer as ClobSigner,
			this.creds as ApiKeyCreds,
			headerArgs,
			this.useServerTime ? await this.getServerTime() : undefined,
		);

		let results: UserEarning[] = [];
		let next_cursor = INITIAL_CURSOR;
		while (next_cursor !== END_CURSOR) {
			const params = {
				date,
				signature_type: this.orderBuilder.signatureType,
				next_cursor,
			};

			const response = await this.get(`${this.host}${endpoint}`, {
				headers,
				params,
			});
			next_cursor = response.next_cursor;
			results = [...results, ...response.data];
		}
		return results;
	}

	public async getTotalEarningsForUserForDay(date: string): Promise<TotalUserEarning[]> {
		this.canL2Auth();

		const endpoint = GET_TOTAL_EARNINGS_FOR_USER_FOR_DAY;
		const headerArgs = {
			method: GET,
			requestPath: endpoint,
		};

		const headers = await createL2Headers(
			this.signer as ClobSigner,
			this.creds as ApiKeyCreds,
			headerArgs,
			this.useServerTime ? await this.getServerTime() : undefined,
		);

		const params = {
			date,
			signature_type: this.orderBuilder.signatureType,
		};

		return await this.get(`${this.host}${endpoint}`, {
			headers,
			params,
		});
	}

	public async getUserEarningsAndMarketsConfig(
		date: string,
		order_by = "",
		position = "",
		no_competition = false,
	): Promise<UserRewardsEarning[]> {
		this.canL2Auth();

		const endpoint = GET_REWARDS_EARNINGS_PERCENTAGES;
		const headerArgs = {
			method: GET,
			requestPath: endpoint,
		};

		const headers = await createL2Headers(
			this.signer as ClobSigner,
			this.creds as ApiKeyCreds,
			headerArgs,
			this.useServerTime ? await this.getServerTime() : undefined,
		);

		let results: UserRewardsEarning[] = [];
		let next_cursor = INITIAL_CURSOR;
		while (next_cursor !== END_CURSOR) {
			const params = {
				date,
				signature_type: this.orderBuilder.signatureType,
				next_cursor,
				order_by,
				position,
				no_competition,
			};

			const response = await this.get(`${this.host}${endpoint}`, {
				headers,
				params,
			});
			next_cursor = response.next_cursor;
			results = [...results, ...response.data];
		}
		return results;
	}

	public async getRewardPercentages(): Promise<RewardsPercentages> {
		this.canL2Auth();

		const endpoint = GET_LIQUIDITY_REWARD_PERCENTAGES;
		const headerArgs = {
			method: GET,
			requestPath: endpoint,
		};

		const headers = await createL2Headers(
			this.signer as ClobSigner,
			this.creds as ApiKeyCreds,
			headerArgs,
			this.useServerTime ? await this.getServerTime() : undefined,
		);

		const _params = {
			signature_type: this.orderBuilder.signatureType,
		};

		return this.get(`${this.host}${endpoint}`, { headers, params: _params });
	}

	public async getCurrentRewards(): Promise<MarketReward[]> {
		let results: MarketReward[] = [];
		let next_cursor = INITIAL_CURSOR;
		while (next_cursor !== END_CURSOR) {
			const response = await this.get(`${this.host}${GET_REWARDS_MARKETS_CURRENT}`, {
				params: { next_cursor },
			});
			next_cursor = response.next_cursor;
			results = [...results, ...response.data];
		}
		return results;
	}

	public async getRawRewardsForMarket(conditionId: string): Promise<MarketReward[]> {
		let results: MarketReward[] = [];
		let next_cursor = INITIAL_CURSOR;
		while (next_cursor !== END_CURSOR) {
			const response = await this.get(`${this.host}${GET_REWARDS_MARKETS}${conditionId}`, {
				params: { next_cursor },
			});
			next_cursor = response.next_cursor;
			results = [...results, ...response.data];
		}
		return results;
	}

	public async calculateMarketPrice(
		tokenID: string,
		side: Side,
		amount: number,
		orderType: OrderType = OrderType.FOK,
	): Promise<number> {
		const book = await this.getOrderBook(tokenID);
		if (!book) {
			throw new Error("no orderbook");
		}
		if (side === Side.BUY) {
			if (!book.asks) {
				throw new Error("no match");
			}
			return calculateBuyMarketPrice(book.asks, amount, orderType);
		} else {
			if (!book.bids) {
				throw new Error("no match");
			}
			return calculateSellMarketPrice(book.bids, amount, orderType);
		}
	}

	public async createBuilderApiKey(): Promise<BuilderApiKey> {
		this.canL2Auth();

		const endpoint = CREATE_BUILDER_API_KEY;
		const headerArgs = {
			method: POST,
			requestPath: endpoint,
		};

		const headers = await createL2Headers(
			this.signer as ClobSigner,
			this.creds as ApiKeyCreds,
			headerArgs,
			this.useServerTime ? await this.getServerTime() : undefined,
		);

		return this.post(`${this.host}${endpoint}`, { headers });
	}

	public async getBuilderApiKeys(): Promise<BuilderApiKeyResponse[]> {
		this.canL2Auth();

		const endpoint = GET_BUILDER_API_KEYS;
		const headerArgs = {
			method: GET,
			requestPath: endpoint,
		};

		const headers = await createL2Headers(
			this.signer as ClobSigner,
			this.creds as ApiKeyCreds,
			headerArgs,
			this.useServerTime ? await this.getServerTime() : undefined,
		);

		return this.get(`${this.host}${endpoint}`, { headers });
	}

	public async revokeBuilderApiKey(): Promise<any> {
		this.canL2Auth();

		const endpoint = REVOKE_BUILDER_API_KEY;
		const headerArgs = {
			method: DELETE,
			requestPath: endpoint,
		};

		const headers = await createL2Headers(
			this.signer as ClobSigner,
			this.creds as ApiKeyCreds,
			headerArgs,
			this.useServerTime ? await this.getServerTime() : undefined,
		);

		return this.del(`${this.host}${endpoint}`, { headers });
	}

	public async getMarketTradesEvents(conditionID: string): Promise<MarketTradeEvent[]> {
		return this.get(`${this.host}${GET_MARKET_TRADES_EVENTS}${conditionID}`);
	}

	private canL1Auth(): void {
		if (this.signer === undefined) {
			throw L1_AUTH_UNAVAILABLE_ERROR;
		}
	}

	private canL2Auth(): void {
		if (this.signer === undefined) {
			throw L1_AUTH_UNAVAILABLE_ERROR;
		}

		if (this.creds === undefined) {
			throw L2_AUTH_NOT_AVAILABLE;
		}
	}

	private isBuilderOrder(builderCode?: string): boolean {
		return builderCode !== undefined && builderCode !== bytes32Zero;
	}

	private async getBuilderTakerFeeRate(builderCode?: string): Promise<number> {
		if (!this.isBuilderOrder(builderCode)) return 0;

		await this.ensureBuilderFeeRateCached(builderCode);
		return this.builderFeeRates[builderCode ?? ""]?.taker ?? 0;
	}

	private async adjustBuyAmountForBalance(
		tokenID: string,
		amount: number,
		price: number,
		userUSDCBalance: number,
		builderCode?: string,
	): Promise<number> {
		await this._ensureMarketInfoCached(tokenID);
		const builderTakerFeeRate = await this.getBuilderTakerFeeRate(builderCode);

		return adjustBuyAmountForFees(
			amount,
			price,
			userUSDCBalance,
			this.feeInfos[tokenID].rate,
			this.feeInfos[tokenID].exponent,
			builderTakerFeeRate,
			this.feeSlippage,
		);
	}

	private async _ensureMarketInfoCached(tokenID: string): Promise<void> {
		if (tokenID in this.feeInfos) return;

		if (!(tokenID in this.tokenConditionMap)) {
			const result = await this.get(`${this.host}${GET_MARKET_BY_TOKEN}${tokenID}`);
			if (!result?.condition_id) {
				throw new Error(`failed to resolve condition id for token ${tokenID}`);
			}
			this.tokenConditionMap[tokenID] = result.condition_id as string;
		}

		await this.getClobMarketInfo(this.tokenConditionMap[tokenID]);
	}

	private async ensureBuilderFeeRateCached(builderCode?: string): Promise<void> {
		if (!builderCode || builderCode === bytes32Zero) return;
		if (builderCode in this.builderFeeRates) return;

		const result = await this.get(`${this.host}${GET_BUILDER_FEES}${builderCode}`);
		this.builderFeeRates[builderCode] = {
			maker: result.builder_maker_fee_rate_bps / BUILDER_FEES_BPS,
			taker: result.builder_taker_fee_rate_bps / BUILDER_FEES_BPS,
		};
	}

	private async _resolveTickSize(tokenID: string, tickSize?: TickSize): Promise<TickSize> {
		const minTickSize = await this.getTickSize(tokenID);
		if (tickSize) {
			if (isTickSizeSmaller(tickSize, minTickSize)) {
				throw new Error(
					`invalid tick size (${tickSize}), minimum for the market is ${minTickSize}`,
				);
			}
		} else {
			tickSize = minTickSize;
		}
		return tickSize;
	}

	private async _resolveFeeRateBps(tokenID: string, userFeeRateBps?: number): Promise<number> {
		const marketFeeRateBps = await this.getFeeRateBps(tokenID);
		if (
			marketFeeRateBps > 0 &&
			userFeeRateBps !== undefined &&
			userFeeRateBps !== marketFeeRateBps
		) {
			throw new Error(
				`invalid user provided fee rate: ${userFeeRateBps}, fee rate for the market must be ${marketFeeRateBps}`,
			);
		}
		return marketFeeRateBps;
	}

	private async resolveVersion(forceUpdate: boolean = false): Promise<number> {
		// Use cached version if given
		if (!forceUpdate && this.cachedVersion !== undefined) {
			return this.cachedVersion;
		}

		// Query API and cache the result
		const apiVersion = await this.getVersion();
		this.cachedVersion = apiVersion;

		return apiVersion;
	}

	private async _retryOnVersionUpdate(retryFunc: () => Promise<unknown>) {
		const version = await this.resolveVersion();

		for (let attempt = 0; attempt < 2; attempt++) {
			await retryFunc();

			// no need to retry if version is unchanged
			if (version === (await this.resolveVersion())) break;
		}
	}

	private _isOrderVersionMismatch(resp: ClobErrorResponseBody) {
		const error = resp?.error;
		if (!error) return false;
		const message = typeof error === "string" ? error : JSON.stringify(error);
		return message.includes(ORDER_VERSION_MISMATCH_ERROR);
	}

	private throwIfError(result: any): any {
		if (this.throwOnError && result && typeof result === "object" && "error" in result) {
			const msg =
				typeof result.error === "string" ? result.error : JSON.stringify(result.error);
			throw new ApiError(msg, result.status, result);
		}
		return result;
	}

	// http methods
	private async get(endpoint: string, options?: RequestOptions, skipThrow = false) {
		const result = await get(endpoint, options);
		return skipThrow ? result : this.throwIfError(result);
	}

	private async post(endpoint: string, options?: RequestOptions, skipThrow = false) {
		const result = await post(endpoint, options, this.retryOnError);
		return skipThrow ? result : this.throwIfError(result);
	}

	private async del(endpoint: string, options?: RequestOptions, skipThrow = false) {
		const result = await del(endpoint, options);
		return skipThrow ? result : this.throwIfError(result);
	}
}
